import {
  createEntityAdapter,
  createSlice,
  isAnyOf,
  PayloadAction,
  Update,
} from '@reduxjs/toolkit';

import {
  MessageType,
  type ListMessagesRequest,
  type PagingMetadataV2,
} from '@wix/ambassador-innovation-widget-v1-message/types';

import { senderIsOwner } from '@/utils/sender';

import {
  getMessagesHistory,
  getEditorMockMessages,
  getConversationId,
} from './messagesThunks';
import { Message } from './model';

const MANUAL_INTERVENTION_MAX_AGE_MS = 10 * 60 * 1000;

export const messagesAdapter = createEntityAdapter<Message>({
  sortComparer: (a, b) => {
    if (a.sortKey && b.sortKey) {
      return (a.sortKey ?? '').localeCompare(b.sortKey ?? '');
    }

    if (a.createdDate && b.createdDate) {
      return a.createdDate - b.createdDate;
    }
    return 0;
  },
});

const initialState = messagesAdapter.getInitialState<{
  request: 'idle' | 'loading' | 'loadingHistory' | 'error';
  conversationIdRequest: 'idle' | 'loading';
  pendingMessages: number;
  currentRequestId?: string;
  isTyping: boolean;
  awaitingAnswer: boolean;
  manualInterventionActive: boolean;
  manualInterventionPending: boolean;
  hasUnread: boolean;
  firstUnreadMessageId: string | null;
  pagingMetadata: PagingMetadataV2 | null;
  conversationId: string | null;
  historyContactFormIds: string[];
  pendingScrollToMessageId: string | null;
  isSendingWhatsAppSocialButtonMessage: boolean;
  // Bumped every resetConversation. Used instead of conversationId to tell
  // whether an in-flight async operation (history load, WhatsApp CTA send)
  // is still current — conversationId alone can't distinguish two different
  // generations of the same conversation if a merge/reset ever returns to
  // the same id mid-flight.
  conversationGeneration: number;
}>({
  request: 'idle',
  conversationIdRequest: 'idle',
  pendingMessages: 0,
  isTyping: false,
  awaitingAnswer: false,
  manualInterventionActive: false,
  manualInterventionPending: false,
  hasUnread: false,
  firstUnreadMessageId: null,
  pagingMetadata: null,
  conversationId: null,
  historyContactFormIds: [],
  pendingScrollToMessageId: null,
  isSendingWhatsAppSocialButtonMessage: false,
  conversationGeneration: 0,
});

const localAdapterSelectors = messagesAdapter.getSelectors();

function hasRecentOwnerMessage(messages: Message[]) {
  const recentThreshold = Date.now() - MANUAL_INTERVENTION_MAX_AGE_MS;

  return messages.some(
    (message) =>
      senderIsOwner(message.sender) &&
      Boolean(message.createdDate && message.createdDate >= recentThreshold),
  );
}

function syncManualInterventionFromHistory(state: typeof initialState) {
  state.manualInterventionActive = hasRecentOwnerMessage(
    localAdapterSelectors.selectAll(state),
  );
  state.manualInterventionPending = false;
}

function addHistoryContactFormIds(
  state: typeof initialState,
  messages: Message[],
) {
  const contactFormIds = messages
    .filter((message) => message.messageType === MessageType.CONTACT_FORM)
    .map((message) => message.id);

  if (!contactFormIds.length) {
    return;
  }

  state.historyContactFormIds = Array.from(
    new Set([...(state.historyContactFormIds ?? []), ...contactFormIds]),
  );
}

export const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    replace: messagesAdapter.setAll,
    addMessage: messagesAdapter.addOne,
    addOptimisticMessage: (state, { payload }: PayloadAction<Message>) => {
      // The WhatsApp CTA has its own send-time dedup/in-flight guard and
      // doesn't need newMessageEvent's dedup gate, so it's excluded from the
      // shared pendingMessages count — otherwise sending it would suppress
      // unrelated visitor/streamed-assistant/special-message duplexer
      // events for anyone else viewing the conversation while it's in flight.
      if (payload.messageType !== MessageType.WHATSAPP_SOCIAL_BUTTON) {
        state.pendingMessages++;
      }
      messagesAdapter.addOne(state, payload);
    },
    resetConversation: (state, { payload }: PayloadAction<string>) => {
      state.pendingMessages = 0;
      state.request = 'idle';
      state.currentRequestId = undefined;
      state.isTyping = false;
      state.awaitingAnswer = false;
      state.manualInterventionActive = false;
      state.manualInterventionPending = false;
      state.hasUnread = false;
      state.firstUnreadMessageId = null;
      state.pagingMetadata = null;
      state.conversationId = payload;
      state.historyContactFormIds = [];
      // A pending scroll target from the previous conversation must not
      // carry over — it may no longer exist, or point at an unrelated
      // message once the new conversation's history loads.
      state.pendingScrollToMessageId = null;
      // A CTA send left in flight across a conversation switch (e.g. an
      // owner-side merge while sendWhatsAppSocialButtonMessage awaits
      // history) would otherwise leave this stuck true until its own
      // timeout, silently ignoring clicks in the new conversation.
      state.isSendingWhatsAppSocialButtonMessage = false;
      // See conversationGeneration's own comment above initialState — this
      // is what actually distinguishes this reset from any other, even one
      // that returns to the same conversationId.
      state.conversationGeneration += 1;
    },
    resetMessages: (
      state,
      {
        payload: { messages, pagingMetadata },
      }: PayloadAction<{
        pagingMetadata?: PagingMetadataV2;
        messages: Message[];
      }>,
    ) => {
      messagesAdapter.setAll(state, messages);
      state.firstUnreadMessageId = null;
      state.pagingMetadata = pagingMetadata ?? null;
      state.historyContactFormIds = [];
      syncManualInterventionFromHistory(state);
    },
    addMessages: (
      state,
      {
        payload,
      }: PayloadAction<{
        pagingMetadata?: PagingMetadataV2;
        messages: Message[];
      }>,
    ) => {
      messagesAdapter.addMany(state, payload.messages);
      state.pagingMetadata = payload.pagingMetadata!;
      addHistoryContactFormIds(state, payload.messages);
      syncManualInterventionFromHistory(state);
    },
    updateMessage: (
      state,
      { payload }: PayloadAction<Update<Message, string>>,
    ) => {
      // Message has got an id means it's no longer an optimistic. WhatsApp
      // CTAs never incremented pendingMessages (see addOptimisticMessage), so
      // confirming one must not decrement it either — otherwise it can steal
      // a decrement from an unrelated message that's still genuinely pending.
      const entity = state.entities[payload.id];
      if (
        payload.id &&
        payload.changes.id &&
        payload.id !== payload.changes.id &&
        entity?.messageType !== MessageType.WHATSAPP_SOCIAL_BUTTON &&
        state.pendingMessages > 0 // In case someone uses addMessage instead of addOptimisticMessage
      ) {
        state.pendingMessages--;
      }
      messagesAdapter.updateOne(state, payload);
    },
    appendMessageText: (
      state,
      { payload: { id, text } }: PayloadAction<{ id: string; text: string }>,
    ) => {
      const target = state.entities[id];
      if (!target) {
        return;
      }
      const body = (target.body = target.body || {});
      body.mainText = (body.mainText ?? '') + text;
    },
    addOrUpdateMessage: messagesAdapter.upsertOne,
    // Used to roll back a still-optimistic message (e.g. addMessage failed to
    // persist) — also clears its pendingMessages slot so newMessageEvent
    // doesn't keep suppressing real-time updates indefinitely.
    removeMessage: (state, { payload }: PayloadAction<string>) => {
      const entity = state.entities[payload];
      // A rollback from a stale request (e.g. a previous conversation, after
      // resetConversation) must not touch the current conversation's entities
      // or pendingMessages count — resetConversation doesn't clear entities,
      // so a late call could otherwise decrement an unrelated counter.
      if (!entity || entity.conversationId !== state.conversationId) {
        return;
      }
      // WhatsApp CTAs never incremented pendingMessages (see
      // addOptimisticMessage), so rolling one back must not decrement it
      // either — same reasoning as updateMessage above.
      if (
        entity.messageType !== MessageType.WHATSAPP_SOCIAL_BUTTON &&
        state.pendingMessages > 0
      ) {
        state.pendingMessages--;
      }
      messagesAdapter.removeOne(state, payload);
    },
    setConversationId: (state, { payload }: PayloadAction<string>) => {
      state.conversationId = payload;
    },
    setIsTyping: (
      state,
      { payload }: PayloadAction<boolean | null | undefined>,
    ) => {
      state.isTyping = !!payload;
    },
    setAwaitingAnswer: (
      state,
      { payload }: PayloadAction<boolean | null | undefined>,
    ) => {
      state.awaitingAnswer = !!payload;
    },
    setManualInterventionActive: (
      state,
      { payload }: PayloadAction<boolean | null | undefined>,
    ) => {
      state.manualInterventionActive = !!payload;
    },
    setManualInterventionPending: (
      state,
      { payload }: PayloadAction<boolean | null | undefined>,
    ) => {
      state.manualInterventionPending = !!payload;
    },
    setUnread: (state, { payload }: PayloadAction<boolean>) => {
      state.hasUnread = payload;
    },
    markUnreadFromMessage: (state, { payload }: PayloadAction<string>) => {
      state.hasUnread = true;
      state.firstUnreadMessageId ??= payload;
    },
    clearFirstUnreadMessageId: (state) => {
      state.firstUnreadMessageId = null;
    },
    setIsSendingWhatsAppSocialButtonMessage: (
      state,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.isSendingWhatsAppSocialButtonMessage = payload;
    },
    setPendingScrollToMessageId: (
      state,
      { payload }: PayloadAction<string>,
    ) => {
      state.pendingScrollToMessageId = payload;
    },
    clearPendingScrollToMessageId: (state) => {
      state.pendingScrollToMessageId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getConversationId.pending, (state) => {
      state.conversationIdRequest = 'loading';
    });
    builder.addCase(getConversationId.fulfilled, (state) => {
      state.conversationIdRequest = 'idle';
    });
    builder.addMatcher(
      isAnyOf(getMessagesHistory.pending, getEditorMockMessages.pending),
      (state, action) => {
        const isHistoryRequest = Boolean(
          (action.meta.arg as ListMessagesRequest)?.cursorPaging?.cursor,
        );
        state.currentRequestId = action.meta.requestId;
        state.request = isHistoryRequest ? 'loadingHistory' : 'loading';
      },
    );
    builder.addMatcher(
      isAnyOf(getMessagesHistory.fulfilled, getEditorMockMessages.fulfilled),
      (state, action) => {
        if (state.currentRequestId === action.meta.requestId) {
          state.currentRequestId = undefined;
          state.request = 'idle';
        }
      },
    );
  },
});

export const { reducer: messagesReducer } = messagesSlice;
